import { useState, useMemo, useEffect } from 'react';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { Box, Typography, AppBar, Tab, Tabs, Button, Autocomplete, TextField } from '@mui/material';
import { useSelector } from 'react-redux';

import {
  numToCurrency,
  downloadExcel,
  downloadPdf,
  convertAllDates,
  effectivePriceAfterRebate,
  exportPrelaunchSummary,
  getOutdoorType,
  netHST,
  getSalesStatusCodes,
} from '../../../utils/Functions';
import { IPriceList, IUnit, IUnitData, IUnitHistory } from '../../../types/unit';
import StandardTable from '../../tables/StandardTable';
import { selectProject } from '../../../features/project/projectSlice';
import { customTierUnits, customTiers } from '../../../utils/CustomSettings';
import LevelSummary from './LevelSummary';
import RangeSummary from './RangeSummary';
import { Flex, FlexBetween } from '../../../commonStyles';
import { selectUser } from '../../../features/auth/authSlice';

const TypeSummary = (props: ChildProps) => {
  const { allUnits } = props;
  const user = useSelector(selectUser);
  const project = useSelector(selectProject);
  const salesStatusCodes = useMemo(() => getSalesStatusCodes(project), [project.status]);
  const [units, setUnits] = useState<IUnit[]>([]);
  const [priceLists, setPriceLists] = useState<IPriceList[]>([]);
  const [tierType, setTierType] = useState<string>('Overall');
  const [statusType, setStatusType] = useState<string>('All');
  const [draftType, setDraftType] = useState<string>('Current')
  const unitTypes = units
    .map((unit) => unit.unitType)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort();

  const [getUnits, { loading }] = useLazyQuery<IUnitData>(UNITS, {
    variables: { filter: { project: project._id } },
    onCompleted: (data) => {
      setUnits(data.unitMany);
    },
  });

  useQuery(GETPRICELISTS, {
    variables: { filter: { project: project._id } },
    onCompleted: (data) => {
      setPriceLists(data.priceListMany);
    },
  });

  useEffect(() => {
    if (allUnits) {
      setUnits(allUnits);
    } else {
      if (draftType === 'Current') {
        getUnits();
      }
    }
  }, [allUnits, draftType]);

  const handleTierChange = (e: any, value: string) => {
    setTierType(value);
  };

  const handleStatusChange = (e: any, value: string) => {
    setStatusType(value);
  };

  const columns = useMemo(() => {
    let dataColumns = [
      {
        Header: 'Unit Type',
        accessor: (rowData: any) => rowData.unitType,
      },
      {
        Header: 'Average PPSF',
        accessor: (rowData: any) => rowData.averagePpsf,
      },
      {
        Header: 'Average Price',
        accessor: (rowData: any) => rowData.averagePrice,
      },
      {
        Header: 'Average Size',
        accessor: (rowData: any) => rowData.averageSize,
      },
      {
        Header: 'Lowest Price',
        accessor: (rowData: any) => rowData.lowestPrice,
      },
      {
        Header: 'Highest Price',
        accessor: (rowData: any) => rowData.highestPrice,
      },
      {
        Header: 'Median',
        accessor: (rowData: any) => rowData.median,
      },
      {
        Header: 'Units Total',
        accessor: (rowData: any) => `${rowData.unitsTotal} (${Math.round((rowData.unitsTotal / units.length) * 100)}%)`,
      },
      {
        Header: 'Cumulative',
        accessor: (rowData: any) => rowData.cumulative,
      },
      {
        Header: 'Units Available',
        accessor: (rowData: any) => rowData.unitsAvailable,
      },
      {
        Header: 'Units Pending',
        accessor: (rowData: any) => rowData.unitsPending,
      },
      {
        Header: 'Units Sold (C, F)',
        accessor: (rowData: any) =>
          `${rowData.unitsSold} (${rowData.unitsTotal ? Math.round((rowData.unitsSold / rowData.unitsTotal) * 10000) / 100 : '0'}%)`,
      },
      {
        Header: 'Sold Revenue',
        accessor: (rowData: any) => `${rowData.soldRevenue}`,
      },
      {
        Header: 'Total Revenue',
        accessor: (rowData: any) => `${rowData.totalRevenue} (${rowData.totalRevenuePercentage})`,
      },
    ];

    return dataColumns;
  }, [units]);

  const modelColumns = useMemo(() => {
    let dataColumns = [
      {
        Header: 'Unit Type',
        accessor: (rowData: any) => rowData.unitType,
      },
      {
        Header: 'Model Type',
        accessor: (rowData: any) => rowData.modelType,
      },
      {
        Header: 'Average PPSF',
        accessor: (rowData: any) => rowData.averagePpsf,
      },
      {
        Header: 'Average Price',
        accessor: (rowData: any) => rowData.averagePrice,
      },
      {
        Header: 'Average Size',
        accessor: (rowData: any) => rowData.averageSize,
      },
      {
        Header: 'Lowest Price',
        accessor: (rowData: any) => rowData.lowestPrice,
      },
      {
        Header: 'Highest Price',
        accessor: (rowData: any) => rowData.highestPrice,
      },
      {
        Header: 'Median',
        accessor: (rowData: any) => rowData.median,
      },
      {
        Header: 'Units Total',
        accessor: (rowData: any) => `${rowData.unitsTotal} (${Math.round((rowData.unitsTotal / units.length) * 100)}%)`,
      },
      {
        Header: 'Cumulative',
        accessor: (rowData: any) => rowData.cumulative,
      },
      {
        Header: 'Units Available',
        accessor: (rowData: any) => rowData.unitsAvailable,
      },
      {
        Header: 'Units Pending',
        accessor: (rowData: any) => rowData.unitsPending,
      },
      {
        Header: 'Units Sold (C, F)',
        accessor: (rowData: any) =>
          `${rowData.unitsSold} (${rowData.unitsTotal ? Math.round((rowData.unitsSold / rowData.unitsTotal) * 10000) / 100 : '0'}%)`,
      },
      {
        Header: 'Sold Revenue',
        accessor: (rowData: any) => `${rowData.soldRevenue}`,
      },
      {
        Header: 'Total Revenue',
        accessor: (rowData: any) => `${rowData.totalRevenue} (${rowData.totalRevenuePercentage})`,
      },
    ];

    return dataColumns;
  }, [units]);

  const outdoorColumns = useMemo(() => {
    let dataColumns = [
      {
        Header: 'Unit Type',
        accessor: (rowData: any) => rowData.unitType,
      },
      {
        Header: 'Outdoor Type',
        accessor: (rowData: any) => rowData.outdoorType,
      },
      {
        Header: 'Average PPSF',
        accessor: (rowData: any) => rowData.averagePpsf,
      },
      {
        Header: 'Average Price',
        accessor: (rowData: any) => rowData.averagePrice,
      },
      {
        Header: 'Average Size',
        accessor: (rowData: any) => rowData.averageSize,
      },
      {
        Header: 'Lowest Price',
        accessor: (rowData: any) => rowData.lowestPrice,
      },
      {
        Header: 'Highest Price',
        accessor: (rowData: any) => rowData.highestPrice,
      },
      {
        Header: 'Median',
        accessor: (rowData: any) => rowData.median,
      },
      {
        Header: 'Units Total',
        accessor: (rowData: any) => `${rowData.unitsTotal} (${Math.round((rowData.unitsTotal / units.length) * 100)}%)`,
      },
      {
        Header: 'Cumulative',
        accessor: (rowData: any) => rowData.cumulative,
      },
      {
        Header: 'Units Available',
        accessor: (rowData: any) => rowData.unitsAvailable,
      },
      {
        Header: 'Units Pending',
        accessor: (rowData: any) => rowData.unitsPending,
      },
      {
        Header: 'Units Sold (C, F)',
        accessor: (rowData: any) =>
          `${rowData.unitsSold} (${rowData.unitsTotal ? Math.round((rowData.unitsSold / rowData.unitsTotal) * 10000) / 100 : '0'}%)`,
      },
      {
        Header: 'Sold Revenue',
        accessor: (rowData: any) => `${rowData.soldRevenue}`,
      },
      {
        Header: 'Total Revenue',
        accessor: (rowData: any) => `${rowData.totalRevenue} (${rowData.totalRevenuePercentage})`,
      },
    ];

    return dataColumns;
  }, [units]);

  const bathroomColumns = useMemo(() => {
    let dataColumns = [
      {
        Header: 'Unit Type',
        accessor: (rowData: any) => rowData.unitType,
      },
      {
        Header: 'Bathrooms',
        accessor: (rowData: any) => rowData.bathrooms,
      },
      {
        Header: 'Average PPSF',
        accessor: (rowData: any) => rowData.averagePpsf,
      },
      {
        Header: 'Average Price',
        accessor: (rowData: any) => rowData.averagePrice,
      },
      {
        Header: 'Average Size',
        accessor: (rowData: any) => rowData.averageSize,
      },
      {
        Header: 'Lowest Price',
        accessor: (rowData: any) => rowData.lowestPrice,
      },
      {
        Header: 'Highest Price',
        accessor: (rowData: any) => rowData.highestPrice,
      },
      {
        Header: 'Median',
        accessor: (rowData: any) => rowData.median,
      },
      {
        Header: 'Units Total',
        accessor: (rowData: any) => `${rowData.unitsTotal} (${Math.round((rowData.unitsTotal / units.length) * 100)}%)`,
      },
      {
        Header: 'Cumulative',
        accessor: (rowData: any) => rowData.cumulative,
      },
      {
        Header: 'Units Available',
        accessor: (rowData: any) => rowData.unitsAvailable,
      },
      {
        Header: 'Units Pending',
        accessor: (rowData: any) => rowData.unitsPending,
      },
      {
        Header: 'Units Sold (C, F)',
        accessor: (rowData: any) =>
          `${rowData.unitsSold} (${rowData.unitsTotal ? Math.round((rowData.unitsSold / rowData.unitsTotal) * 10000) / 100 : '0'}%)`,
      },
      {
        Header: 'Sold Revenue',
        accessor: (rowData: any) => `${rowData.soldRevenue}`,
      },
      {
        Header: 'Total Revenue',
        accessor: (rowData: any) => `${rowData.totalRevenue} (${rowData.totalRevenuePercentage})`,
      },
    ];

    return dataColumns;
  }, [units]);

  // Shared label/id column config and widths for the Unit Type Summary / Bathrooms tables' Excel
  // and PDF exports (via `download` below) as well as the combined Prelaunch Summary export.
  const summaryColumns = useMemo(
    () => [
      { label: 'Unit Types', id: 'unitType' },
      { label: 'Model Type', id: 'modelType' },
      { label: 'Bathroom', id: 'bathrooms' },
      { label: 'Outdoor Type', id: 'outdoorType' },
      { label: 'Average Price', id: 'averagePrice' },
      { label: 'Average Size', id: 'averageSize' },
      { label: 'Average PPSF', id: 'averagePpsf' },
      { label: 'Lowest Price', id: 'lowestPrice' },
      { label: 'Highest Price', id: 'highestPrice' },
      { label: 'Median', id: 'median' },
      { label: 'Units Total', id: 'unitsTotal' },
      { label: 'Cumulative', id: 'cumulative' },
      { label: 'Units Available', id: 'unitsAvailable' },
      { label: 'Units Pending', id: 'unitsPending' },
      { label: 'Units Sold (C, F)', id: 'unitsSold' },
      { label: 'Sold Revenue', id: 'soldRevenue' },
      { label: 'Total Revenue', id: 'totalRevenue' },
    ],
    [],
  );

  const summaryWidths: Record<string, number> = {
    unitType: 15,
    modelType: 15,
    bathrooms: 15,
    outdoorType: 15,
    averagePrice: 15,
    averageSize: 15,
    averagePpsf: 15,
    lowestPrice: 15,
    highestPrice: 15,
    median: 15,
    unitsTotal: 15,
    cumulative: 15,
    unitsAvailable: 15,
    unitsPending: 15,
    unitsSold: 15,
    soldRevenue: 15,
    totalRevenue: 15,
  };

  // Trimmed column sets for the combined Prelaunch Summary export only — the on-screen tables and
  // their individual per-table exports (via `download` above) keep using the full `summaryColumns`.
  const prelaunchUnitTypeColumns = useMemo(
    () => [
      { label: 'Unit Types', id: 'unitType' },
      { label: 'Average Price', id: 'averagePrice' },
      { label: 'Average Size', id: 'averageSize' },
      { label: 'Average PPSF', id: 'averagePpsf' },
      { label: 'Lowest Price', id: 'lowestPrice' },
      { label: 'Highest Price', id: 'highestPrice' },
      { label: 'Median', id: 'median' },
      { label: 'Units Total', id: 'unitsTotal' },
      { label: 'Cumulative', id: 'cumulative' },
      { label: 'Units Available', id: 'unitsAvailable' },
      { label: 'Total Revenue', id: 'totalRevenue' },
    ],
    [],
  );

  const prelaunchBathroomColumns = useMemo(
    () => [
      { label: 'Unit Types', id: 'unitType' },
      { label: 'Bathroom', id: 'bathrooms' },
      { label: 'Average Price', id: 'averagePrice' },
      { label: 'Average Size', id: 'averageSize' },
      { label: 'Average PPSF', id: 'averagePpsf' },
      { label: 'Lowest Price', id: 'lowestPrice' },
      { label: 'Highest Price', id: 'highestPrice' },
      { label: 'Median', id: 'median' },
      { label: 'Units Total', id: 'unitsTotal' },
      { label: 'Cumulative', id: 'cumulative' },
      { label: 'Units Available', id: 'unitsAvailable' },
      { label: 'Total Revenue', id: 'totalRevenue' },
    ],
    [],
  );

  const download = (type: string, data: any) => {
    let unitColumns = summaryColumns;
    let widths = summaryWidths;

    let pdfWidths = {
      unitType: 200,
      modelType: 200,
      bathrooms: 200,
      outdoorType: 200,
      averagePrice: 200,
      averageSize: 200,
      averagePpsf: 200,
      lowestPrice: 200,
      highestPrice: 200,
      median: 200,
      unitsTotal: 200,
      cumulative: 200,
      unitsAvailable: 200,
      unitsPending: 200,
      unitsSold: 200,
      soldRevenue: 15,
      totalRevenue: 15,
    };

    let sheetTitle = `${project.name} - Unit Summary`;

    if (type === 'excel') {
      downloadExcel([data], [unitColumns], [], [[widths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([data], [unitColumns], [], [pdfWidths], [sheetTitle], sheetTitle, project.logoGetUrl);
    }
  };

  // useNetHst is only ever passed true for the Prelaunch Summary download, and only actually
  // changes anything for a project.netHst project — every on-screen table call (which never
  // passes it) and every other project keep showing gross basePrice as before.
  const priceFor = (unit: IUnit, useNetHst?: boolean) => {
    if (statusType === 'After HST Rebate') {
      // effectivePriceAfterRebate already returns the net-of-HST price for this project, so
      // useNetHst must not run it through netHST a second time here - doing so silently
      // double-discounted the Prelaunch Summary export relative to what the on-screen table
      // (which never passes useNetHst) shows for the same tab.
      return effectivePriceAfterRebate(unit.basePrice, project);
    }
    let price = unit.basePrice;
    if (useNetHst && project.netHst) {
      price = netHST(price, project);
    }
    return price;
  };

  const unitTypeData = (useNetHst?: boolean) => {
    let allUnits = units;

    if (tierType) {
      allUnits = units.filter((unit: IUnit) => customTierUnits(project._id, units, tierType).includes(unit.tier));
    }

    if (statusType !== 'Overall') {
      if (statusType === 'Sold') {
        allUnits = allUnits.filter((unit: IUnit) => salesStatusCodes.includes(unit.status));
      }
      if (statusType === 'Available') {
        allUnits = allUnits.filter((unit: IUnit) => !salesStatusCodes.includes(unit.status));
      }
      if (statusType === 'After HST Rebate') {
        allUnits = allUnits.filter((unit: IUnit) => !salesStatusCodes.includes(unit.status));
      }
    }

    let unitData = unitTypes.map((type, index: number) => {
      const totalRevenueAllUnits: number = allUnits.map((unit) => priceFor(unit, useNetHst)).reduce((a, b) => a + b, 0);
      const validUnits = allUnits.filter((unit) => unit.unitType === type);
      const validUnitPrices = allUnits.filter((unit) => unit.unitType === type).map((unit) => priceFor(unit, useNetHst));
      const totalPrice = validUnits.map((unit) => priceFor(unit, useNetHst)).reduce((a, b) => a + b, 0);
      const totalSize = validUnits.map((unit) => unit.size).reduce((a, b) => a + b, 0);
      const mid = Math.floor(validUnitPrices.length / 2);
      const nums = [...validUnitPrices].sort((a, b) => a - b);
      const typeRevenue = allUnits
        .filter((unit) => unit.unitType === type)
        .map((unit) => priceFor(unit, useNetHst))
        .reduce((a, b) => a + b, 0);
      const soldRevenue = allUnits
        .filter((unit) => unit.unitType === type && salesStatusCodes.includes(unit.status))
        .map((unit) => priceFor(unit, useNetHst))
        .reduce((a, b) => a + b, 0);
      const totalUnits = allUnits.filter((unit) => unit.unitType === type).length;
      const availableUnits = allUnits.filter((unit) => unit.unitType === type && !salesStatusCodes.includes(unit.status)).length;
      const pendingUnits = allUnits.filter(
        (unit) => unit.unitType === type && (unit.status === 'P' || unit.status === 'O' || unit.status === 'S')
      ).length;
      const soldUnits = allUnits.filter((unit) => unit.unitType === type && salesStatusCodes.includes(unit.status)).length;

      let cumulative = 0;
      for (let i = 0; i <= index; i++) {
        cumulative += allUnits.filter((unit) => unit.unitType === unitTypes[i]).length;
      }

      return {
        unitType: type,
        averagePrice: validUnits.length ? numToCurrency.format(totalPrice / validUnits.length) : 'N/A',
        averageSize: validUnits.length ? (totalSize / validUnits.length).toFixed(2) : 'N/A',
        averagePpsf: totalSize ? numToCurrency.format(totalPrice / totalSize) : 'N/A',
        lowestPrice: validUnitPrices.length > 0 ? numToCurrency.format(Math.min(...validUnitPrices)) : 'N/A',
        highestPrice: validUnitPrices.length > 0 ? numToCurrency.format(Math.max(...validUnitPrices)) : 'N/A',
        median:
          validUnitPrices.length === 0
            ? 'N/A'
            : validUnitPrices.length % 2 !== 0
            ? numToCurrency.format(nums[mid])
            : numToCurrency.format((nums[mid - 1] + nums[mid]) / 2),
        soldRevenue: numToCurrency.format(soldRevenue),
        soldRevenuePercentage: isNaN(Math.round((soldRevenue / typeRevenue) * 10000) / 100)
          ? '$0'
          : Math.round((soldRevenue / totalRevenueAllUnits) * 10000) / 100 + '%',
        totalRevenue: numToCurrency.format(typeRevenue),
        totalRevenuePercentage: isNaN(Math.round((typeRevenue / totalRevenueAllUnits) * 10000) / 100)
          ? '$0'
          : Math.round((typeRevenue / totalRevenueAllUnits) * 10000) / 100 + '%',
        unitsTotal: totalUnits,
        cumulative: `${cumulative} (${Math.round((cumulative / units.length) * 100)}%)`,
        unitsAvailable: availableUnits,
        unitsPending: pendingUnits,
        unitsSold: soldUnits,
      };
    });

    const allUnitPrices = allUnits.map((unit) => priceFor(unit, useNetHst));
    const totalRevenue = allUnits.map((unit) => priceFor(unit, useNetHst)).reduce((a, b) => a + b, 0);
    const totalSize = allUnits.map((unit) => unit.size).reduce((a, b) => a + b, 0);
    const totalSoldRevenue = allUnits
      .filter((unit) => salesStatusCodes.includes(unit.status))
      .map((unit) => priceFor(unit, useNetHst))
      .reduce((a, b) => a + b, 0);
    const totalAvailableUnits = allUnits.filter((unit) => !salesStatusCodes.includes(unit.status)).length;
    const totalPendingUnits = allUnits.filter((unit) => unit.status === 'P' || unit.status === 'O' || unit.status === 'S').length;
    const totalSoldUnits = allUnits.filter((unit) => salesStatusCodes.includes(unit.status)).length;
    const midAll = Math.floor(allUnitPrices.length / 2);
    const numsAll = [...allUnitPrices].sort((a, b) => a - b);

    let totalUnitData = {
      unitType: 'Total',
      averagePrice: numToCurrency.format(totalRevenue / units.length),
      averageSize: (totalSize / units.length).toFixed(2),
      averagePpsf: numToCurrency.format(totalRevenue / totalSize),
      lowestPrice: allUnitPrices.length > 0 ? numToCurrency.format(Math.min(...allUnitPrices)) : 'N/A',
      highestPrice: allUnitPrices.length > 0 ? numToCurrency.format(Math.max(...allUnitPrices)) : 'N/A',
      median:
        allUnitPrices.length === 0
          ? 'N/A'
          : allUnitPrices.length % 2 !== 0
          ? numToCurrency.format(numsAll[midAll])
          : numToCurrency.format((numsAll[midAll - 1] + numsAll[midAll]) / 2),
      soldRevenue: numToCurrency.format(totalSoldRevenue),
      soldRevenuePercentage: isNaN(Math.round((totalSoldRevenue / totalRevenue) * 10000) / 100)
        ? '$0'
        : Math.round((totalSoldRevenue / totalRevenue) * 10000) / 100 + '%',
      totalRevenue: numToCurrency.format(totalRevenue),
      totalRevenuePercentage: '100%',
      unitsTotal: allUnits.length,
      cumulative: allUnits.length,
      unitsAvailable: totalAvailableUnits,
      unitsPending: totalPendingUnits,
      unitsSold: totalSoldUnits,
    };

    return [...unitData, totalUnitData];
  };

  const data = (unitField: string, useNetHst?: boolean) => {
    const getFieldValue = (unit: any) => (unitField === 'outdoorType' ? getOutdoorType(unit.outdoor) : unit[unitField]);
    let allUnits = units;

    if (tierType) {
      allUnits = units.filter((unit: IUnit) => customTierUnits(project._id, units, tierType).includes(unit.tier));
    }

    if (statusType !== 'Overall') {
      if (statusType === 'Sold') {
        allUnits = units.filter((unit: IUnit) => salesStatusCodes.includes(unit.status));
      }
      if (statusType === 'Available') {
        allUnits = units.filter((unit: IUnit) => !salesStatusCodes.includes(unit.status));
      }
      if (statusType === 'After HST Rebate') {
        allUnits = units.filter((unit: IUnit) => !salesStatusCodes.includes(unit.status));
      }
    }

    let unitData = unitTypes
      .map((type, index: number) => {
        const totalUnits = allUnits.filter((unit) => unit.unitType === type);
        const unitFilteredType = totalUnits.map((unit: any) => getFieldValue(unit));
        const dataTypes = unitFilteredType.filter((v, i, a) => a.indexOf(v) === i);
        const typeData = dataTypes.map((dataType) => {
          const totalRevenueAllUnits: number = allUnits.map((unit) => priceFor(unit, useNetHst)).reduce((a, b) => a + b, 0);
          const validUnits = allUnits.filter((unit: any) => unit.unitType === type && getFieldValue(unit) === dataType);
          const validUnitPrices = validUnits.map((unit) => priceFor(unit, useNetHst));
          const totalPrice = validUnits.map((unit) => priceFor(unit, useNetHst)).reduce((a, b) => a + b, 0);
          const totalSize = validUnits.map((unit) => unit.size).reduce((a, b) => a + b, 0);
          const mid = Math.floor(validUnitPrices.length / 2);
          const nums = [...validUnitPrices].sort((a, b) => a - b);
          const typeRevenue = allUnits
            .filter((unit: any) => unit.unitType === type && getFieldValue(unit) === dataType)
            .map((unit) => priceFor(unit, useNetHst))
            .reduce((a, b) => a + b, 0);
          const soldRevenue = allUnits
            .filter((unit: any) => unit.unitType === type && getFieldValue(unit) === dataType && salesStatusCodes.includes(unit.status))
            .map((unit) => priceFor(unit, useNetHst))
            .reduce((a, b) => a + b, 0);
          const totalUnits = validUnits.length;
          const availableUnits = allUnits.filter(
            (unit: any) => unit.unitType === type && getFieldValue(unit) === dataType && !salesStatusCodes.includes(unit.status)
          ).length;
          const pendingUnits = allUnits.filter(
            (unit: any) => getFieldValue(unit) === dataType && (unit.status === 'P' || unit.status === 'O' || unit.status === 'S')
          ).length;
          const soldUnits = allUnits.filter(
            (unit: any) => getFieldValue(unit) === dataType && salesStatusCodes.includes(unit.status)
          ).length;

          return {
            modelType: unitField === 'modelType' ? dataType : '',
            outdoorType: unitField === 'outdoorType' ? dataType : '',
            bathrooms: unitField === 'bathroom' ? dataType : '',
            unitType: type,
            averagePrice: numToCurrency.format(totalPrice / validUnits.length),
            averageSize: (totalSize / validUnits.length).toFixed(0),
            averagePpsf: numToCurrency.format(totalPrice / totalSize),
            lowestPrice: validUnitPrices.length > 0 ? numToCurrency.format(Math.min(...validUnitPrices)) : 'N/A',
            highestPrice: validUnitPrices.length > 0 ? numToCurrency.format(Math.max(...validUnitPrices)) : 'N/A',
            median:
              validUnitPrices.length === 0
                ? 'N/A'
                : validUnitPrices.length % 2 !== 0
                ? numToCurrency.format(nums[mid])
                : numToCurrency.format((nums[mid - 1] + nums[mid]) / 2),
            soldRevenue: numToCurrency.format(soldRevenue),
            soldRevenuePercentage: isNaN(Math.round((soldRevenue / typeRevenue) * 10000) / 100),
            totalRevenue: numToCurrency.format(typeRevenue),
            totalRevenuePercentage: isNaN(Math.round((typeRevenue / totalRevenueAllUnits) * 10000) / 100)
              ? '$0'
              : Math.round((typeRevenue / totalRevenueAllUnits) * 10000) / 100 + '%',
            unitsTotal: totalUnits,
            unitsAvailable: availableUnits,
            unitsPending: pendingUnits,
            unitsSold: soldUnits,
            percentageSold: `${Math.round((soldUnits / totalUnits) * 10000) / 100}%`,
          };
        });
        return typeData;
      })
      .flat();

    let cumulativeCount = 0;
    unitData = unitData.map((row: any) => {
      cumulativeCount += row.unitsTotal;
      return { ...row, cumulative: `${cumulativeCount} (${Math.round((cumulativeCount / allUnits.length) * 100)}%)` };
    });

    const allUnitPrices = allUnits.map((unit) => priceFor(unit, useNetHst));
    const totalRevenue = allUnits.map((unit) => priceFor(unit, useNetHst)).reduce((a, b) => a + b, 0);
    const totalSize = allUnits.map((unit) => unit.size).reduce((a, b) => a + b, 0);
    const totalSoldRevenue = allUnits
      .filter((unit) => salesStatusCodes.includes(unit.status))
      .map((unit) => priceFor(unit, useNetHst))
      .reduce((a, b) => a + b, 0);
    const totalAvailableUnits = allUnits.filter((unit) => !salesStatusCodes.includes(unit.status)).length;
    const totalPendingUnits = allUnits.filter((unit) => unit.status === 'P' || unit.status === 'O' || unit.status === 'S').length;
    const totalSoldUnits = allUnits.filter((unit) => salesStatusCodes.includes(unit.status)).length;
    const midAll = Math.floor(allUnitPrices.length / 2);
    const numsAll = [...allUnitPrices].sort((a, b) => a - b);

    let totalUnitData = {
      modelType: 'N/A',
      unitType: 'Total',
      outdoorType: 'N/A',
      bathrooms: 'N/A',
      averagePrice: numToCurrency.format(totalRevenue / units.length),
      averageSize: (totalSize / units.length).toFixed(2),
      averagePpsf: numToCurrency.format(totalRevenue / totalSize),
      lowestPrice: allUnitPrices.length > 0 ? numToCurrency.format(Math.min(...allUnitPrices)) : 'N/A',
      highestPrice: allUnitPrices.length > 0 ? numToCurrency.format(Math.max(...allUnitPrices)) : 'N/A',
      median:
        allUnitPrices.length === 0
          ? 'N/A'
          : allUnitPrices.length % 2 !== 0
          ? numToCurrency.format(numsAll[midAll])
          : numToCurrency.format((numsAll[midAll - 1] + numsAll[midAll]) / 2),
      soldRevenue: numToCurrency.format(totalSoldRevenue),
      soldRevenuePercentage: isNaN(Math.round((totalSoldRevenue / totalRevenue) * 10000) / 100)
        ? '$0'
        : Math.round((totalSoldRevenue / totalRevenue) * 10000) / 100 + '%',
      totalRevenue: numToCurrency.format(totalRevenue),
      totalRevenuePercentage: '100%',
      unitsTotal: allUnits.length,
      cumulative: allUnits.length,
      unitsAvailable: totalAvailableUnits,
      unitsPending: totalPendingUnits,
      unitsSold: totalSoldUnits,
    };

    return [...unitData, totalUnitData];
  };

  const priceChanges = () => {
    let unitColumns = [
      {
        label: 'Suite',
        id: 'suite',
      },
      {
        label: 'Opening Day Price',
        id: 'originalPrice',
      },
      {
        label: 'Date',
        id: 'date',
      },
      {
        label: 'Price From',
        id: 'priceFrom',
      },
      {
        label: 'Price To',
        id: 'priceTo',
      },
      {
        label: 'User',
        id: 'user',
      },
    ];

    let widths = {
      suite: 15,
      originalPrice: 15,
      date: 15,
      priceFrom: 15,
      priceTo: 15,
      user: 15,
    };

    let sheetTitle = `${project.name} - Price Change History`;

    let data = units
      .map((unit: IUnit) => {
        let history = unit.history
          .filter((history: IUnitHistory) => history.priceFrom && history.priceTo)
          .map((history: any) => {
            return {
              suite: unit.suite,
              originalPrice: numToCurrency.format(unit.originalPrice),
              date: convertAllDates(history.timestamp, 'PPpp'),
              priceFrom: numToCurrency.format(history.priceFrom),
              priceTo: numToCurrency.format(history.priceTo),
              user: history.user.fullName,
            };
          });
        return history;
      })
      .flat();

    downloadExcel([data], [unitColumns], [], [[widths]], [sheetTitle], sheetTitle);
  };

  // Combined workbook (IPL, Unit Type Summary, Unit Type Summary Bathrooms, $50,000 Price Range)
  // for whatever draft is currently selected above, or the current units if none is selected.
  // Passing `project` (and `useNetHst: true` into the summary data) is what makes the download's
  // numbers net-of-HST for a project.netHst project only — the on-screen tables above never pass
  // useNetHst, so they're unaffected.
  //
  // The IPL and Price Range sheets are built from a plain unit list (not the pre-summarized rows
  // unitTypeData/data return), so they need their own copy of the same tierType/statusType
  // filtering those functions already apply internally - otherwise those two sheets would always
  // show every unit regardless of which tab (All/Sold/Available/After HST Rebate) is selected,
  // while Unit Type Summary and Bathrooms would correctly reflect just that tab.
  const prelaunchSummary = () => {
    let filteredUnits = units;
    if (tierType) {
      filteredUnits = units.filter((unit: IUnit) => customTierUnits(project._id, units, tierType).includes(unit.tier));
    }
    if (statusType === 'Sold') {
      filteredUnits = filteredUnits.filter((unit: IUnit) => salesStatusCodes.includes(unit.status));
    }
    if (statusType === 'Available' || statusType === 'After HST Rebate') {
      filteredUnits = filteredUnits.filter((unit: IUnit) => !salesStatusCodes.includes(unit.status));
    }

    let draftLabel = draftType !== 'Current' ? ` ${draftType}` : '';
    let fileName = `${project.name}${draftLabel} ${convertAllDates(new Date(), 'P')} Prelaunch Summary.xlsx`;
    exportPrelaunchSummary(
      fileName,
      project.name,
      filteredUnits,
      unitTypeData(true),
      prelaunchUnitTypeColumns,
      data('bathroom', true),
      prelaunchBathroomColumns,
      summaryWidths,
      project,
    );
  };

  return (
    <Box sx={{ m: 2 }}>
      {customTiers(project._id, units).length ? (
        <AppBar position="sticky" sx={{ mb: 2 }}>
          <Tabs value={tierType} scrollButtons="auto" onChange={handleTierChange} aria-label="scrollable auto tabs unit type">
            {customTiers(project._id, units).map((tier: string, index: number) => {
              return <Tab key={index} value={tier} label={tier} />;
            })}
            ;
          </Tabs>
        </AppBar>
      ) : null}
      <AppBar position="sticky" sx={{ mb: 2 }}>
        <Tabs value={statusType} scrollButtons="auto" onChange={handleStatusChange} aria-label="scrollable auto tabs unit type">
          {['All', 'Sold', 'Available', 'After HST Rebate'].map((tier: string, index: number) => {
            return <Tab key={index} value={tier} label={tier} />;
          })}
          ;
        </Tabs>
      </AppBar>
      <Box sx={{ mb: 2 }}>
        <FlexBetween sx={{ mb: 2 }}>
          <Typography gutterBottom variant="h2">
            Unit Type Summary
          </Typography>
          {user.type === 'Manager' || user.type === 'Admin' ? (
            <Flex>
              {priceLists.length ? (
                <Box sx={{ mr: 2 }}>
                  <Autocomplete
                    sx={{ width: '200px' }}
                    options={[{ name: "Current", draft: units}, ...priceLists]}
                    getOptionLabel={(option: any) => option.name}
                    disableClearable={false}
                    freeSolo={false}
                    onChange={(event: any, newValue: any | null) => {
                      if (newValue) {
                        setDraftType(newValue.name);
                        setUnits(newValue.draft);
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label="Drafts" size="small" />}
                  />
                </Box>
              ) : null}
              <Box sx={{ mr: 1 }}>
                <Button variant="contained" color="primary" onClick={() => priceChanges()}>
                  Price Change History
                </Button>
              </Box>
              <Box>
                <Button variant="contained" color="primary" onClick={() => prelaunchSummary()}>
                  Prelaunch Summary
                </Button>
              </Box>
            </Flex>
          ) : null}
        </FlexBetween>
        <StandardTable download={download} columns={columns} data={unitTypeData()} loading={loading} />
      </Box>
      {units.some((unit: IUnit) => getOutdoorType(unit.outdoor)) ? (
        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom variant="h2">
            Unit Type Summary (Outdoor)
          </Typography>
          <StandardTable download={download} columns={outdoorColumns} data={data('outdoorType')} loading={loading} />
        </Box>
      ) : null}
      <Box sx={{ mb: 2 }}>
        <Typography gutterBottom variant="h2">
          Unit Type Summary (Bathrooms)
        </Typography>
        <StandardTable download={download} columns={bathroomColumns} data={data('bathroom')} loading={loading} />
      </Box>
      <Box>
        <Typography gutterBottom variant="h2">
          Model Type Summary
        </Typography>
        <StandardTable download={download} columns={modelColumns} data={data('modelType')} loading={loading} />
      </Box>
      <Box sx={{ my: 2 }}>
        <RangeSummary units={units} tierType={tierType} statusType={statusType} />
      </Box>
      <Box sx={{ my: 2 }}>
        <LevelSummary units={units} tierType={tierType} statusType={statusType} />
      </Box>
    </Box>
  );
};

interface ChildProps {
  allUnits?: IUnit[];
}

const UNITS = gql`
  query unitMany($filter: FilterFindManyUnitInput) {
    unitMany(filter: $filter, limit: 10000) {
      _id
      suite
      unit
      modelType
      unitType
      bathroom
      size
      basePrice
      originalPrice
      level
      exposure
      ceilingHeight
      outdoor {
        name
        size
      }
      history {
        type
        description
        priceFrom
        priceTo
        timestamp
        user {
          _id
          fullName
        }
      }
      status
      tier
    }
  }
`;

const GETPRICELISTS = gql`
  query priceListMany($filter: FilterFindManyPriceListInput) {
    priceListMany(filter: $filter, limit: 10000) {
      _id
      name
      project {
        _id
      }
      draft {
        _id
        status
        suite
        unit
        unitId
        level
        modelType
        unitType
        basePrice
        size
        outdoor {
          name
          size
        }
        bathroom
        exposure
        ceilingHeight
        col
        row
        tier
        stackCol
        stackRow
      }
    }
  }
`;

export default TypeSummary;
